import { Theme } from "../config/theme.js";

export function spawnMatchParticles(scene, x, y, colorHex) {
  // colorHex may be a Phaser integer (from tileColorToHex) or a CSS string
  const tileColor = (typeof colorHex === 'number')
    ? colorHex
    : Phaser.Display.Color.HexStringToColor(
        (colorHex && typeof colorHex === 'string') ? colorHex : Theme.vfx.matchParticleColor
      ).color;

  // Central white flash — sharp burst at impact point
  const flash = scene.add.circle(x, y, 7, 0xffffff, 1).setDepth(95);
  scene.tweens.add({
    targets: flash,
    scale: 3,
    alpha: 0,
    duration: 200,
    ease: 'Expo.Out',
    onComplete: () => flash.destroy(),
  });

  // Colored shards — 6 particles flying outward in a ring
  const COUNT = 6;
  for (let i = 0; i < COUNT; i++) {
    const angle = (i / COUNT) * Math.PI * 2;
    const dist  = 15 + Math.random() * 16;
    const r     = 2 + Math.random() * 2;
    const shard = scene.add.circle(x, y, r, tileColor, 1).setDepth(95);
    scene.tweens.add({
      targets: shard,
      x: x + Math.cos(angle) * dist,
      y: y + Math.sin(angle) * dist,
      alpha: 0,
      scale: 0.2,
      duration: 230 + Math.random() * 80,
      ease: 'Expo.Out',
      onComplete: () => shard.destroy(),
    });
  }
}

// multiplier — the combo number (e.g. 3 for "COMBO ×3!")
// themeColor  — current region primary color hex (e.g. '#ff4500') used when cfg.useThemeColor is true
export function spawnComboText(scene, x, y, multiplier, themeColor = '#ffffff') {
  // ── Find the highest matching level config ──────────────────────
  const levels = [...(Theme.vfx.comboLevels || [])];
  levels.sort((a, b) => b.minMultiplier - a.minMultiplier);
  const cfg = levels.find(c => multiplier >= c.minMultiplier);

  // ── Resolve style values (with legacy fallback) ─────────────────
  const fontFamily = cfg?.font
    ? `'${cfg.font}', system-ui, Arial`
    : 'system-ui, Arial';
  const fontSize   = cfg?.fontSize        || '28px';
  const color      = cfg
    ? (cfg.useThemeColor ? themeColor : cfg.color)
    : Theme.vfx.comboTextColor;
  const stroke     = cfg?.strokeColor     || Theme.vfx.comboTextStroke;
  const strokeW    = cfg?.strokeThickness ?? 4;

  // ── Render ──────────────────────────────────────────────────────
  const t = scene.add.text(x, y, `COMBO ×${multiplier}!`, {
    fontFamily,
    fontSize,
    color,
    stroke,
    strokeThickness: strokeW,
  }).setOrigin(0.5);

  // ── Optional sound (wired when audio is set up) ─────────────────
  if (cfg?.sound) {
    try { scene.sound.play(cfg.sound); } catch (_) {}
  }

  scene.tweens.add({
    targets:  t,
    y:        y - 60,
    alpha:    0,
    scale:    1.5,
    duration: Theme.vfx.comboDuration,
    ease:     'Back.Out',
    onComplete: () => t.destroy(),
  });
}

export function spawnSkillBurst(scene, x, y, _tintOverride) {
  // usa a cor do tema; _tintOverride mantido por compatibilidade
  const color = Phaser.Display.Color.HexStringToColor(Theme.vfx.skillBurstColor).color;
  const burst = scene.add.circle(x, y, 12, color);

  scene.tweens.add({
    targets: burst,
    scale: 3,
    alpha: 0,
    duration: Theme.vfx.skillBurstDuration,
    ease: "Expo.Out",
    onComplete: () => burst.destroy(),
  });
}

// ── Beam fly-through no Event Strip ao activar skill ─────────────────────────
// monsterId: 'fogo' | 'raio' | 'jato' | 'terra' | 'controlo'
// y: posição vertical do event strip (this.eventText.y em LevelScene)
// level: nível do monstro (1 / 2 / 3) — afeta altura, tint e flash de câmara
export function spawnBeamVFX(scene, monsterId, y, level = 1) {
  const key = `beam-${monsterId}`;
  if (!scene.textures.exists(key)) return;

  const W = scene.scale.width;

  const beamH = level >= 3 ? 170 : level === 2 ? 125 : 90;
  const tint  = level >= 3 ? 0xff8833 : level === 2 ? 0xffdd88 : 0xffffff;

  // ── Lv1 — quick clean sweep ───────────────────────────────────────────────
  if (level === 1) {
    const beam = scene.add.image(-W * 0.3, y, key)
      .setDisplaySize(W * 1.1, beamH).setAlpha(0).setDepth(190)
      .setOrigin(0.5).setTint(tint).setBlendMode(Phaser.BlendModes.ADD);

    scene.tweens.add({
      targets: beam, x: W / 2, alpha: 1, duration: 180, ease: 'Expo.Out',
      onComplete: () => {
        scene.tweens.add({
          targets: beam, x: W * 1.3, alpha: 0, duration: 350, ease: 'Expo.In',
          onComplete: () => beam.destroy(),
        });
      },
    });
    return;
  }

  // ── Lv2 — charged: shake + hold + echo ───────────────────────────────────
  if (level === 2) {
    const beam = scene.add.image(-W * 0.3, y, key)
      .setDisplaySize(W * 1.1, beamH).setAlpha(0).setDepth(190)
      .setOrigin(0.5).setTint(tint).setBlendMode(Phaser.BlendModes.ADD);

    scene.tweens.add({
      targets: beam, x: W / 2, alpha: 1, duration: 140, ease: 'Expo.Out',
      onComplete: () => {
        // shake on land — suppressed per design
        // hold at centre, then exit
        scene.time.delayedCall(120, () => {
          // echo beam trails behind
          const echo = scene.add.image(W / 2, y, key)
            .setDisplaySize(W * 1.1, beamH * 0.6).setAlpha(0.35).setDepth(189)
            .setOrigin(0.5).setTint(tint).setBlendMode(Phaser.BlendModes.ADD);

          scene.tweens.add({
            targets: beam, x: W * 1.3, alpha: 0, duration: 280, ease: 'Expo.In',
            onComplete: () => beam.destroy(),
          });
          scene.tweens.add({
            targets: echo, x: W * 1.5, alpha: 0, duration: 420, ease: 'Expo.In',
            onComplete: () => echo.destroy(),
          });
        });
      },
    });
    return;
  }

  // ── Lv3 — overdrive: flash + explosive entry + hold-pulse + afterimage ────
  scene.cameras.main.flash(180, 255, 150, 50);

  const beam = scene.add.image(-W * 0.3, y, key)
    .setDisplaySize(W * 1.1, beamH).setAlpha(0).setDepth(190)
    .setOrigin(0.5).setTint(tint).setBlendMode(Phaser.BlendModes.ADD);

  scene.tweens.add({
    targets: beam, x: W / 2, alpha: 1, duration: 90, ease: 'Power4.Out',
    onComplete: () => {
      // pulse alpha while held (charged feel)
      const pulse = scene.tweens.add({
        targets: beam, alpha: { from: 0.75, to: 1 },
        duration: 80, yoyo: true, repeat: 2, ease: 'Sine.InOut',
      });

      // particle burst at centre
      _spawnCentreBurst(scene, W / 2, y, tint);

      scene.time.delayedCall(280, () => {
        pulse.stop();

        // afterimage stays behind as beam exits
        const after = scene.add.image(W / 2, y, key)
          .setDisplaySize(W * 1.1, beamH * 0.5).setAlpha(0.4).setDepth(189)
          .setOrigin(0.5).setTint(tint).setBlendMode(Phaser.BlendModes.ADD);

        scene.tweens.add({
          targets: beam, x: W * 1.3, alpha: 0, duration: 220, ease: 'Power4.In',
          onComplete: () => beam.destroy(),
        });
        scene.tweens.add({
          targets: after, x: W * 1.6, alpha: 0, duration: 400, ease: 'Expo.In',
          onComplete: () => after.destroy(),
        });
      });
    },
  });
}

function _spawnCentreBurst(scene, x, y, tint) {
  for (let i = 0; i < 6; i++) {
    const angle  = (i / 6) * Math.PI * 2;
    const dist   = Phaser.Math.Between(30, 80);
    const dot    = scene.add.circle(x, y, Phaser.Math.Between(3, 7), tint, 1)
      .setDepth(191).setBlendMode(Phaser.BlendModes.ADD);
    scene.tweens.add({
      targets: dot,
      x: x + Math.cos(angle) * dist,
      y: y + Math.sin(angle) * dist,
      alpha: 0,
      scale: 0.2,
      duration: Phaser.Math.Between(220, 380),
      ease: 'Expo.Out',
      onComplete: () => dot.destroy(),
    });
  }
}

export function spawnFailEffect(scene, x, y) {
  const txt = scene.add.text(x, y, "MISS", {
    fontSize: "32px",
    color: Theme.vfx.failTextColor,
    fontStyle: "bold",
  }).setOrigin(0.5);

  scene.cameras.main.shake(350, 0.01);

  scene.tweens.add({
    targets: txt,
    alpha: 0,
    scale: 2,
    duration: Theme.vfx.failDuration,
    onComplete: () => txt.destroy(),
  });
}
